import { createAsyncThunk } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import authApi from "../../authApi";
import {
  setMatrix,
  setMatrixLoading,
  updateMatrixCell,
  setHolidayEvents,
  setFilters,
  setError,
} from "../../Slice/holidayCalendarModule/HolidayPlannerSlice";
import { showSnackbar } from "../../Slice/snackbar/snackbarSlice";

const handleError = (dispatch, error, defaultMsg) => {
  const errorMsg = error?.response?.data?.msg || defaultMsg;
  dispatch(setError([errorMsg]));
  dispatch(showSnackbar({ message: errorMsg, type: "error" }));
  console.error("API Error:", errorMsg, error);
};

const buildFilterParams = filters => {
  const params = {};

  if (filters.startDate) {
    params.from = filters.startDate.split("T")[0];
  } else {
    params.from = dayjs().startOf("month").format("YYYY-MM-DD");
  }

  if (filters.endDate) {
    params.to = filters.endDate.split("T")[0];
  } else {
    params.to = dayjs().endOf("month").format("YYYY-MM-DD");
  }

  if (filters.showAbsentOnly) params.show_unavailable_only = true;
  if (filters.showPresentOnly) params.show_available_only = true;
  if (filters.sourceLanguage) params.source_lang = filters.sourceLanguage;
  if (filters.targetLanguage) params.target_lang = filters.targetLanguage;
  if (filters.resource) params.user_id = filters.resource;
  if (filters.status) params.status = filters.status;

  return params;
};

export const fetchUserAbsences = createAsyncThunk(
  "holidayPlanner/fetchUserAbsences",
  async (userId, { dispatch }) => {
    try {
      const response = await authApi.get("/absences", { params: { user_id: userId } });
      if (response?.status === 200) {
        return response.data?.data || [];
      }
      return [];
    } catch (error) {
      handleError(dispatch, error, "Error fetching absence list");
      throw error;
    }
  }
);

export const fetchAvailabilityMatrix = createAsyncThunk(
  "holidayPlanner/fetchAvailabilityMatrix",
  async (_, { dispatch, getState }) => {
    try {
      dispatch(setMatrixLoading(true));
      const { filters } = getState().root.HolidayPlannerModule;
      const params = buildFilterParams(filters);
      const response = await authApi.get("/absences/matrix-view", { params });

      if (response?.status === 200) {
        dispatch(
          setMatrix({
            dates: response.data?.dates || [],
            linguists: response.data?.linguists || [],
            cells: response.data?.cells || {},
          })
        );
        dispatch(setError([]));
      }
    } catch (error) {
      handleError(dispatch, error, "Error fetching availability matrix");
    } finally {
      dispatch(setMatrixLoading(false));
    }
  }
);

export const upsertAvailabilityCell = createAsyncThunk(
  "holidayPlanner/upsertAvailabilityCell",
  async (payload, { dispatch, getState }) => {
    const { user_id, date, eStatus, time_from, time_to, vNotes, languages } = payload;
    const uid = String(user_id);
    const prevCell = getState().root.HolidayPlannerModule.matrix.cells[uid]?.[date] || null;

    if (eStatus === "CLEAR") {
      dispatch(updateMatrixCell({ userId: user_id, date, cell: null }));
    } else {
      dispatch(
        updateMatrixCell({
          userId: user_id,
          date,
          cell: {
            absentId: prevCell?.absentId,
            eStatus,
            time_from: time_from || null,
            time_to: time_to || null,
            vNotes: vNotes || "",
            languages: languages || [],
          },
        })
      );
    }

    try {
      const response = await authApi.put("/absences/cell", payload);
      if (response?.status === 200) {
        dispatch(setError([]));
        if (eStatus !== "CLEAR" && response.data?.absentId) {
          const current =
            getState().root.HolidayPlannerModule.matrix.cells[uid]?.[date] || {};
          dispatch(
            updateMatrixCell({
              userId: user_id,
              date,
              cell: { ...current, absentId: response.data.absentId },
            })
          );
        }
        dispatch(
          showSnackbar({
            message: response?.data?.msg || "Saved",
            type: "success",
          })
        );
      }
    } catch (error) {
      if (prevCell) {
        dispatch(updateMatrixCell({ userId: user_id, date, cell: prevCell }));
      } else {
        dispatch(updateMatrixCell({ userId: user_id, date, cell: null }));
      }
      handleError(dispatch, error, "Error saving cell");
      throw error;
    }
  }
);

export const bulkApplyAvailability = createAsyncThunk(
  "holidayPlanner/bulkApplyAvailability",
  async (payload, { dispatch }) => {
    try {
      dispatch(setMatrixLoading(true));
      const response = await authApi.post("/absences/bulk-apply", payload);
      if (response?.status === 200) {
        dispatch(
          showSnackbar({
            message: response?.data?.msg || "Bulk apply successful",
            type: "success",
          })
        );
        dispatch(fetchAvailabilityMatrix());
      }
    } catch (error) {
      handleError(dispatch, error, "Error applying bulk availability");
    } finally {
      dispatch(setMatrixLoading(false));
    }
  }
);

const handleSuccess = (dispatch, response, successMsg) => {
  if (response?.status === 200) {
    dispatch(setHolidayEvents(response?.data?.events || []));
    dispatch(setError([]));
    if (successMsg) {
      dispatch(showSnackbar({ message: successMsg, type: "success" }));
    }
  }
};

export const holidayEventsList = createAsyncThunk(
  "holidayPlanner/holidayEventsList",
  async (_, { dispatch }) => {
    try {
      dispatch(setMatrixLoading(true));
      const response = await authApi.get("/absences/calendar-view");
      handleSuccess(dispatch, response);
    } catch (error) {
      handleError(dispatch, error, "Error fetching holiday events");
    } finally {
      dispatch(setMatrixLoading(false));
    }
  }
);

export const createHolidayAbsence = createAsyncThunk(
  "holidayPlanner/createHolidayAbsence",
  async (payload, { dispatch }) => {
    const { refreshMatrix = true, ...apiPayload } = payload;
    try {
      const response = await authApi.post("/absences", apiPayload);
      if (response?.status === 200 || response?.status === 201) {
        if (refreshMatrix) {
          dispatch(setMatrixLoading(true));
          dispatch(fetchAvailabilityMatrix());
        }
        dispatch(setError([]));
        dispatch(
          showSnackbar({
            message: response?.data?.msg || "Successfully Created",
            type: "success",
          })
        );
        return response.data;
      }
    } catch (error) {
      handleError(dispatch, error, "Error creating absence");
      dispatch(setMatrixLoading(false));
      throw error;
    }
  }
);

export const updateHolidayAbsence = createAsyncThunk(
  "holidayPlanner/updateHolidayAbsence",
  async ({ id, payload }, { dispatch }) => {
    const { refreshMatrix = true, ...apiPayload } = payload;
    try {
      const response = await authApi.put(`/absences/${id}`, apiPayload);
      if (response?.status === 200) {
        if (refreshMatrix) {
          dispatch(setMatrixLoading(true));
          dispatch(fetchAvailabilityMatrix());
        }
        dispatch(setError([]));
        dispatch(
          showSnackbar({
            message: response?.data?.msg || "Successfully Updated",
            type: "success",
          })
        );
        return response.data;
      }
    } catch (error) {
      handleError(dispatch, error, "Error updating absence");
      dispatch(setMatrixLoading(false));
      throw error;
    }
  }
);

export const deleteHolidayAbsence = createAsyncThunk(
  "holidayPlanner/deleteHolidayAbsence",
  async (arg, { dispatch }) => {
    const id = typeof arg === "object" ? arg.id : arg;
    const refreshMatrix = typeof arg === "object" ? arg.refreshMatrix !== false : true;
    try {
      const response = await authApi.delete(`/absences/${id}`);
      if (response?.status === 200) {
        if (refreshMatrix) {
          dispatch(setMatrixLoading(true));
          dispatch(fetchAvailabilityMatrix());
        }
        dispatch(setError([]));
        dispatch(
          showSnackbar({
            message: response?.data?.msg || "Successfully Deleted",
            type: "success",
          })
        );
        return response.data;
      }
    } catch (error) {
      handleError(dispatch, error, "Error deleting absence");
      dispatch(setMatrixLoading(false));
      throw error;
    }
  }
);

export const updateHolidayFilters = createAsyncThunk(
  "holidayPlanner/updateHolidayFilters",
  async (filterUpdates, { dispatch }) => {
    dispatch(setFilters(filterUpdates));
    dispatch(setError([]));
  }
);

export const applyHolidayFilters = createAsyncThunk(
  "holidayPlanner/applyHolidayFilters",
  async (_, { dispatch }) => {
    dispatch(fetchAvailabilityMatrix());
  }
);
